// =============================================================================
// TIER 1: Color Palettes
// Full color scales from design system
// =============================================================================

export const palette = {
  // Neutrals
  neu0: '#FFFFFF',
  neu50: '#F6F6F6',
  neu100: '#E7E7E7',
  neu200: '#D1D1D1',
  neu300: '#B0B0B0',
  neu400: '#888888',
  neu500: '#6D6D6D',
  neu600: '#5D5D5D',
  neu700: '#4F4F4F',
  neu800: '#454545',
  neu900: '#3B3B3B', //font color
  neu999: '#000000',

  // Cre (Coursio Teal)
  cre50: '#EEFFFD',
  cre100: '#C6FFFB',
  cre200: '#8EFFF9',
  cre300: '#4DFBF6',
  cre400: '#19E8E8',
  cre500: '#00C9CC', // Coursio color
  cre600: '#009EA4',
  cre700: '#027D83',
  cre800: '#086167',
  cre900: '#0C5155',

  // Blue
  blu50: '#F0FAFB',
  blu100: '#DAF1F3',
  blu200: '#B9E2E8',
  blu300: '#95D2DB',
  blu400: '#6FC0CF',
  blu500: '#49A9BD',
  blu600: '#2F93A7',
  blu700: '#1A7F94',
  blu800: '#0E6C81',
  blu900: '#105669',

  // Orange
  ora50: '#FFFBEA',
  ora100: '#FFF2C5',
  ora200: '#FFE585',
  ora300: '#FFD246',
  ora400: '#FFBC1B',
  ora500: '#FF9A03',
  ora600: '#E27100',
  ora700: '#BB4D02',
  ora800: '#983B08',
  ora900: '#7C310B',

  // Red
  red50: '#FFF1F1',
  red100: '#FFE4E4',
  red200: '#FFCCCF',
  red300: '#FEA3A7',
  red400: '#FD6F7A',
  red500: '#F63D4F',
  red600: '#E51F3C',
  red700: '#C0102E',
  red800: '#A1102D',
  red900: '#8A112D',

  // Green
  gre50: '#EBFEF5',
  gre100: '#D0FBE6',
  gre200: '#A4F6D2',
  gre300: '#6AEBBB',
  gre400: '#2FD89E',
  gre500: '#0ABF87',
  gre600: '#00A878',
  gre700: '#007C5C',
  gre800: '#036249',
  gre900: '#04503E',

  // Purple
  pur50: '#F6F3FF',
  pur100: '#EFE8FF',
  pur200: '#E4DAFF',
  pur300: '#CAB3FF',
  pur400: '#B188FD',
  pur500: '#9958FA',
  pur600: '#8D36F1',
  pur700: '#7E24DD',
  pur800: '#691DBA',
  pur900: '#571A98',
  pur950: '#350E67',
};

// =============================================================================
// TIER 2: Semantic / Usage Colors
// Components should reference these, not the palette directly
// =============================================================================

const colors = {
  // Brand
  COURSIO: palette.cre500,

  // Primary (Blue)
  PRIMARY: palette.blu800,
  PRIMARY_MEDIUM: palette.blu400,
  PRIMARY_LIGHT: palette.blu100,
  PRIMARY_EXTRA_LIGHT: palette.blu50,

  // Secondary (Orange)
  SECONDARY: palette.ora500,
  SECONDARY_LIGHT: palette.ora200,

  // Neutrals
  NEU_999: palette.neu999,
  NEU_PRIMARY: palette.neu900,
  NEU_600: palette.neu600,
  NEU_400: palette.neu400,
  NEU_300: palette.neu300,
  NEU_200: palette.neu200,
  NEU_100: palette.neu100,
  NEU_50: palette.neu50,
  NEU_0: palette.neu0,

  // Marker (Purple)
  MARKER: palette.pur900,
  MARKER_LIGHT: palette.pur200,

  ACTION: palette.red500,

  // Status
  SUCCESS_GREEN: palette.gre600,
  SUCCESS_LIGHT: palette.gre100,

  ERROR_RED: palette.red500,
  ERROR_LIGHT: palette.red200,

  WARNING_YELLOW: palette.ora500,
};

export const USER_THEME_COLORS = {
  creen: palette.cre500,
  green: '#27AE60',
  blue: '#3498DB',
  blue2: '#2980B9',
  purple: '#9B59B6',
  navyblue: '#34495E',
  gray: '#7F8C8D',
  red: '#C0392B',
  lightgreen: '#66D777',
  teal: '#019695',
  mustard: '#929019',
  darkblue: '#105493',
  golden: '#D1CE62',
  mango: '#F9C332',
  pinktaco: '#EE7CB6',
  pinkster: '#F18D8B',
  orange: '#F39331',
  love: '#A83351',
  purplehaze: '#802C6A',
  raspberry: '#ED4C7C',
};

// =============================================================================
// TIER 3: Color Utilities
// =============================================================================

/** Convert sRGB channel (0-255) to linear RGB (0-1) */
const linearize = (c) => {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

/** WCAG relative luminance from [r, g, b] (0-255) */
const relativeLuminance = (color) => 0.2126 * linearize(color[0]) + 0.7152 * linearize(color[1]) + 0.0722 * linearize(color[2]);

/** WCAG contrast ratio between two luminances */
const contrastRatio = (l1, l2) => {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

const hexToRgb = (color, format) => {
  const r = parseInt(color.substring(1, 3), 16);
  const g = parseInt(color.substring(3, 5), 16);
  const b = parseInt(color.substring(5, 7), 16);

  if (format === 'array') return [r, g, b];
  return { r, g, b };
};

/** Returns white or dark text color — picks whichever has better WCAG contrast */
const contrastingColor = (color) => {
  const lum = relativeLuminance(color);
  const whiteLum = 1; // #FFFFFF
  const darkLum = relativeLuminance(hexToRgb(colors.NEU_PRIMARY, 'array'));
  return contrastRatio(lum, whiteLum) > contrastRatio(lum, darkLum) ? colors.NEU_0 : colors.NEU_PRIMARY;
};

const rgbToHex = (rgbStringOrObj) => {
  let r, g, b;
  if (typeof rgbStringOrObj === 'string') {
    const match = rgbStringOrObj.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) {
      r = parseInt(match[1], 10);
      g = parseInt(match[2], 10);
      b = parseInt(match[3], 10);
    } else {
      return rgbStringOrObj;
    }
  } else if (typeof rgbStringOrObj === 'object' && rgbStringOrObj !== null) {
    r = rgbStringOrObj.r?.value ?? rgbStringOrObj.r;
    g = rgbStringOrObj.g?.value ?? rgbStringOrObj.g;
    b = rgbStringOrObj.b?.value ?? rgbStringOrObj.b;
  }
  if (r == null || g == null || b == null) return '#000000';
  /* eslint-disable-next-line */
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
};

/** Resolve any color input (theme name, hex, rgb string) to hex */
export const getUserThemeColor = (themeC) => {
  if (!themeC) return colors.PRIMARY;
  if (typeof themeC !== 'string') return rgbToHex(themeC);
  if (themeC.match('#')) return themeC;
  if (themeC.match('rgb')) return rgbToHex(themeC);
  return USER_THEME_COLORS[themeC] || colors.PRIMARY;
};

/** Shade a color by amount: positive = lighter (toward white), negative = darker (toward black) */
export const shadeColor = (color, amount) => {
  const { r, g, b } = hexToRgb(getUserThemeColor(color));
  const t = amount > 0 ? 255 * amount : 0;
  const f = amount > 0 ? 1 - amount : 1 + amount;
  return rgbToHex({
    r: Math.round(r * f + t),
    g: Math.round(g * f + t),
    b: Math.round(b * f + t),
  });
};

/** Add alpha to any color (hex or rgb string) → rgba string */
export const toRgba = (color, alpha = 1) => {
  if (!color) return toRgba(colors.PRIMARY, alpha);

  if (color.match('#')) {
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return toRgba(colors.PRIMARY, alpha);

  const [, r, g, b] = match;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const getLighterThemeColor = (themeColor) => shadeColor(themeColor, 0.8);

export const getDarkerThemeColor = (themeColor) => shadeColor(themeColor, -0.5);

/** Returns a CSS linear-gradient string for the branded background effect.
 *  135deg: dark (0%) → base (50%) → light (100%) */
export const themeGradientBg = (themeColor, angle = 135) =>
  `linear-gradient(${angle}deg, ${getDarkerThemeColor(themeColor)} 0%, ${getUserThemeColor(themeColor)} 50%, ${shadeColor(themeColor, 0.6)} 100%)`;

export const getUserThemeContrastColor = (themeColor) => {
  const contrastColor = contrastingColor(hexToRgb(getUserThemeColor(themeColor), 'array'));
  return themeColor ? contrastColor : colors.NEU_PRIMARY;
};

export default colors;
